import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { Tooltip, Button, Modal, Divider, message, Form, Upload, Input } from "antd";
import {
  QuestionCircleOutlined,
  CloseOutlined,
  LoadingOutlined,
  PlusSquareFilled,
  ExclamationCircleOutlined,
  FormOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { format, changeSize } from "@/utils";
import api from "@/api";
import apiAuth from "@/api/auth";
import { publicPath } from "@/utils/base";

const { confirm } = Modal;
import dataJson from "@/../public/data.json";

const prefix = "superDeviceTemplateDetail-info";
import "./index.less";
import type { RcFile, UploadFile, UploadProps } from "antd/es/upload/interface";
import type { UploadChangeParam } from "antd/es/upload";
import { cloneDeep } from "lodash-es";
import { appNameReg } from "@/utils/constant";

function Info() {
  const {
    name_label,
    release,
    please_enter,
    cancelText,
    confirmText,
    asterisk,
    delete_text,
    save_text,
    save_success_text,
    super_device_template,
    super_device_template_detail,
  } = dataJson;

  const navigate = useNavigate();
  const { id } = useParams();
  const { isTampered = false } = useOutletContext<{
    isTampered?: boolean;
  }>() || {};
  const { TextArea } = Input;
  const [baseForm] = Form.useForm();

  const superDeviceTemplateDetail = useSelector((state: any) => state.superDeviceTemplateDetail);
  const isCurrentDetail =
    Boolean(superDeviceTemplateDetail) && String(superDeviceTemplateDetail.id) === String(id);

  const formatTime = (time: any) => {
    if (time === undefined || time === null || time === "") {
      return "";
    }
    return format(new Date(Number(time)), "yyyy年MM月dd日 HH:mm:ss");
  };

  const baseStaticData = {
    name: isCurrentDetail ? superDeviceTemplateDetail?.name || "" : "",
    description: isCurrentDetail ? superDeviceTemplateDetail?.description || "" : "",
    template_ID: isCurrentDetail ? superDeviceTemplateDetail?.id || "" : "",
    nodeCount: isCurrentDetail ? superDeviceTemplateDetail?.nodeCount ?? "" : "",
    ruleCount: isCurrentDetail ? superDeviceTemplateDetail?.ruleCount ?? "" : "",
    createTime: isCurrentDetail ? formatTime(superDeviceTemplateDetail?.createTime) : "",
    updateTime: isCurrentDetail ? formatTime(superDeviceTemplateDetail?.updateTime) : "",
  };

  const [iconImageUrl, setIconImageUrl] = useState<string>(); // 图标url
  const [iconLoading, setIconLoading] = useState(false); // 图标loading
  const iconTokenRef = useRef(null); // 存储鉴权token
  const [isEditName, setIsEditName] = useState<boolean>(false); // 是否可编辑名称
  const [isEditDesc, setIsEditDesc] = useState<boolean>(false); // 是否可编辑说明

  const isKHTemplate = superDeviceTemplateDetail?.appBelong === super_device_template_detail.template_type[0];
  const uploadUrl = api.uploadSuperUrl;

  const handleDelete = () => {
    confirm({
      title: delete_text + super_device_template_detail?.detail_title_dec,
      icon: null,
      width: 480,
      centered: true,
      closable: true,
      closeIcon: <CloseOutlined />,
      okText: confirmText,
      cancelText: cancelText,
      content: (
        <div className="delete-modal-content">
          <ExclamationCircleOutlined style={{ color: "red", marginRight: "4px" }} />
          {super_device_template_detail.app_delete_content1}&quot;{superDeviceTemplateDetail?.name}&quot;
          {super_device_template_detail.app_delete_content2}
        </div>
      ),
      onOk: async () => {
        try {
          const res: any = await api.getSuperDeviceDeleteApi({ ids: [id] });

          if (res?.code == 200) {
            navigate(`${publicPath}/super_device_template`);
          } else {
            message.error(res.msg);
          }
        } catch (errorInfo: any) {
          console.error(errorInfo);
        }
      },
    });
  };
  const handleSave = async () => {
    if (iconLoading) {
      message.warning(release.upload_img_ing);
      return;
    }

    try {
      await baseForm.validateFields();
      const { name, description, template_ID } = baseForm.getFieldsValue();
      const param = {
        id: template_ID,
        name,
        description,
        photoUrl: iconImageUrl,
      };
      const res: any = await api.getSuperDeviceSaveApi(param);
      if (res?.code == 200) {
        message.success(save_success_text);
        navigate(`${publicPath}/super_device_template`);
      } else {
        message.error(res.msg);
      }
    } catch (errorInfo: any) {
      console.error(errorInfo);
    }
  };
  // 图片上传前处理
  const beforeUpload: any = (file: RcFile, { width, height, size, accept = ["png"] }: any) => {
    return new Promise<void>((resolve, reject) => {
      const isAccept = accept.some((item: string) => file.type === `image/${item}`);
      const acceptText = accept.join("、").toUpperCase();
      if (!isAccept) {
        message.error(
          `${release.upload_accept_err.text1}${width}${asterisk}${height}${release.upload_accept_err.text2}${acceptText}${release.upload_accept_err.text3}`
        );

        reject();
        return;
      }
      if (size) {
        const imgSize = file.size;
        if (imgSize > size) {
          const sizeText = changeSize(size);
          message.error(`${release.upload_size_err.text1}${sizeText}${release.upload_size_err.text2}`);

          reject();
          return;
        }
      }
      const _URL = window.URL || window.webkitURL;
      const img = new Image();
      img.src = _URL.createObjectURL(file);

      img.onerror = () => {
        message.error(release.upload_fail);
      };
      img.onload = () => {
        if (img.width != width || img.height != height) {
          message.error(
            `${release.upload_accept_err.text1}${width}${asterisk}${height}${release.upload_accept_err.text2}${acceptText}${release.upload_accept_err.text3}`
          );

          reject();
        } else {
          resolve();
        }
      };
    });
  };

  // 图标新增
  const iconImageChange: UploadProps["onChange"] = (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === "uploading") {
      setIconLoading(true);
      return;
    } else {
      setIconLoading(false);
    }
    if (info.file.status === "done") {
      if (info.file.response.status === 200) {
        const iconUrl = info?.file?.response?.result;
        const iconId = info?.file?.response?.result;

        setIconLoading(false);
        setIconImageUrl(iconUrl);
        baseForm.setFieldValue("template_icon", iconId);
      } else {
        message.error(info?.file?.response?.message);
      }
    } else {
      baseForm.setFieldValue("template_icon", "");
      setIconImageUrl("");
    }
    baseForm.validateFields(["template_icon"]);
  };

  // 获取鉴权token
  const queryIconToken = async () => {
    try {
      const res: any = await apiAuth.getSuperAuth();
      if (res?.code == 200) {
        iconTokenRef.current = res?.data?.superAccessToken;
      } else {
        message.error(res.msg);
      }
    } catch (errorInfo: any) {
      console.error(errorInfo);
    }
  };

  const validateName = (_: any, value: any) => {
    if (!isEditName) {
      return Promise.resolve();
    }
    if (value) {
      return api.checkSuperDeviceNameApi({ name: value, id }).then((resp: any) => {
        if (resp.code === 200) {
          if (resp.data?.exist) {
            return Promise.reject(new Error(super_device_template.name_already));
          }
        } else {
          return Promise.reject(resp.msg);
        }
      });
    } else {
      return Promise.resolve();
    }
  };

  const queryEditName = () => {
    setIsEditName(true);
  };
  const queryEditDesc = () => {
    setIsEditDesc(true);
  };

  // 初始数据：当前模板无数据（如被篡改）或尚未拉取到对应详情时，清空表单，避免沿用上一个模板
  const initData = () => {
    if (isCurrentDetail) {
      const dataCopy = cloneDeep(superDeviceTemplateDetail);
      baseForm.setFieldsValue({
        name: dataCopy?.name || "",
        description: dataCopy?.description || "",
        template_ID: dataCopy?.id || "",
        nodeCount: dataCopy?.nodeCount || "",
        ruleCount: dataCopy?.ruleCount || "",
        createTime: formatTime(dataCopy?.createTime),
        updateTime: formatTime(dataCopy?.updateTime),
      });
      if (dataCopy?.photoUrl) {
        const urlVal = dataCopy?.photoUrl.includes("http") ? dataCopy?.photoUrl : "";
        setIconImageUrl(urlVal);
      } else {
        setIconImageUrl("");
      }
      setIsEditName(false);
      setIsEditDesc(false);
      return;
    }
    baseForm.resetFields();
    setIconImageUrl("");
    setIsEditName(false);
    setIsEditDesc(false);
  };

  useEffect(() => {
    initData();
  }, [superDeviceTemplateDetail, id]);

  return (
    <div className={prefix}>
      <div className={`${prefix}-inner`}>
        <div className={`${prefix}-header`}>
          <div className={`${prefix}-header-text`}>
            <div className={`${prefix}-header-title`}>{super_device_template_detail.menu[0].label}</div>
            <div className={`${prefix}-header-tip`}>
              {super_device_template_detail.detail_title_dec + super_device_template_detail.info_tip}
            </div>
          </div>
          {!isKHTemplate && (
            <div className={`${prefix}-header-btns`}>
              <Button type="primary" className={`${prefix}-content-save`} disabled={isTampered} onClick={handleSave}>
                {save_text}
              </Button>
              <Button
                className={`${prefix}-content-delete`}
                disabled={isTampered || superDeviceTemplateDetail?.ruleCount > 0}
                onClick={handleDelete}
              >
                {delete_text}
              </Button>
            </div>
          )}
        </div>
        <div className={`${prefix}-content`}>
          <div className={`${prefix}-content-title`}>{super_device_template_detail.developer_title}</div>
          <div className={`${prefix}-content-developer`}>
            <Tooltip
              placement="bottomLeft"
              title={super_device_template_detail.developer_tip}
              overlayClassName={`${prefix}-content-developer-tooltip`}
              color="#fff"
              overlayInnerStyle={{
                color: "rgba(0,0,0,0.8)",
                width: 200,
                padding: "10px 16px",
                transform: "translate(-12px, 0)",
              }}
            >
              <QuestionCircleOutlined style={{ color: "#0A59F7", cursor: "pointer", fontSize: 14 }} />
            </Tooltip>
            <div className={`${prefix}-content-developer-id`}>
              <span>{super_device_template_detail.developer_ID}</span>
              {isCurrentDetail ? superDeviceTemplateDetail?.developerId : ""}
            </div>
          </div>
          <Divider />
          <div className={`${prefix}-content-title mt-title`}>{super_device_template_detail.detail_title_dec}</div>
          <Form
            form={baseForm}
            scrollToFirstError={true}
            labelCol={{ span: 3 }}
            layout="horizontal"
            className={`${prefix}-form`}
          >
            <Form.Item label={release.icon_label} name="template_icon">
              <div>
                <Upload
                  accept="image/*"
                  listType="picture-card"
                  showUploadList={false}
                  disabled={iconLoading || isKHTemplate || isTampered}
                  maxCount={1}
                  action={uploadUrl}
                  headers={{
                    "x-access-token": iconTokenRef.current || "",
                  }}
                  beforeUpload={async (e) => {
                    await beforeUpload(e, { width: 256, height: 256, size: 3145728, accept: ["png"] });
                    await queryIconToken();
                  }}
                  onChange={iconImageChange}
                  style={{ margin: 0 }}
                >
                  {iconImageUrl ? (
                    <img src={iconImageUrl} alt="avatar" />
                  ) : iconLoading ? (
                    <LoadingOutlined />
                  ) : (
                    <PlusSquareFilled className={`${prefix}-add-icon`} />
                  )}
                </Upload>
                {!isKHTemplate && <div className={`${prefix}-form-tip`}>{release.icon_tip}</div>}
              </div>
            </Form.Item>
            <Form.Item
              label={super_device_template_detail.template_names}
              name="name"
              rules={[
                { required: true, message: `${please_enter + name_label}` },
                { required: isEditName, validator: validateName },
              ]}
            >
              <>
                {!isKHTemplate ? isEditName && <Input style={{ width: "500px" }} maxLength={64} showCount /> : ""}
                {!isEditName && (
                  <div>
                    <span>{baseStaticData.name}</span>
                    {!isKHTemplate && !isTampered && (
                      <FormOutlined
                        style={{ fontSize: "18px", lineHeight: "10px", marginLeft: "6px" }}
                        onClick={queryEditName}
                      />
                    )}
                  </div>
                )}
              </>
            </Form.Item>
            <Form.Item label={super_device_template_detail.template_ID} name="template_ID">
              {baseStaticData.template_ID}
            </Form.Item>
            <Form.Item label={super_device_template_detail.create_time} name="createTime">
              {baseStaticData.createTime}
            </Form.Item>
            <Form.Item label={super_device_template_detail.update_time} name="updateTime">
              {baseStaticData.updateTime}
            </Form.Item>
            <Form.Item label={super_device_template_detail.joints_number} name="nodeCount">
              <span style={{ color: "#2900ff" }}>{baseStaticData.nodeCount}</span>
            </Form.Item>
            <Form.Item label={super_device_template_detail.business_rules_number} name="ruleCount">
              <span style={{ color: "#2900ff" }}>{baseStaticData.ruleCount}</span>
            </Form.Item>
            <Form.Item
              label={super_device_template_detail.illustrate}
              name="description"
              rules={[{ required: true, message: `${please_enter}` }]}
            >
              {!isKHTemplate
                ? isEditDesc && (
                    <TextArea style={{ width: "500px", resize: "none", height: 90 }} maxLength={200} showCount />
                  )
                : ""}
              {!isEditDesc && (
                <div style={{ display: "flex", alignItems: "end" }}>
                  <div style={{ maxWidth: "500px", maxHeight: "90px", overflow: "auto" }}>
                    {baseStaticData.description}
                  </div>
                  {!isKHTemplate && !isTampered && (
                    <FormOutlined
                      style={{ fontSize: "18px", lineHeight: "10px", marginLeft: "6px" }}
                      onClick={queryEditDesc}
                    />
                  )}
                </div>
              )}
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default Info;
